import React, { useState, useEffect, useRef } from 'react';
import * as api from '../services/api';
import EditableDataTable from '../components/EditableDataTable';

const LEGAL_ROLES = ['legal_officer', 'head_of_legal', 'legal_admin'];
const isLegal = (user) => LEGAL_ROLES.includes(user?.role);

export default function ReviewAdvice({ showToast, editMode, currentUser }) {
  const [reviews, setReviews]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [comments, setComments]         = useState([]);
  const [documents, setDocuments]       = useState([]);

  // Submission form
  const [form, setForm] = useState({
    subject: '', review_type: '', department: '', description: '', urgency: 'medium',
  });
  const [submitFile, setSubmitFile]     = useState(null);
  const [submitting, setSubmitting]     = useState(false);

  // Review workspace
  const [commentForm, setCommentForm]   = useState({ comment: '', annotation_type: 'comment' });
  const [reviewFile, setReviewFile]     = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [accepting, setAccepting]       = useState(false);

  const submitFileRef = useRef();
  const reviewFileRef = useRef();

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const { data } = await api.getRequests({ type: 'document_review' });
      setReviews(data.data || []);
    } catch { showToast('Failed to load reviews'); }
    finally { setLoading(false); }
  }

  async function handleSubmit() {
    if (!form.subject.trim()) { showToast('Please enter a document/subject name'); return; }
    if (!submitFile) { showToast('Please attach the document for review'); return; }
    setSubmitting(true);
    try {
      const { data: newReq } = await api.createRequest({
        subject: form.subject,
        type: 'document_review',
        description: (form.review_type ? '[' + form.review_type + '] ' : '') + form.description,
        priority: form.urgency,
      });
      const fd = new FormData();
      fd.append('file', submitFile);
      fd.append('linked_entity_type', 'request');
      fd.append('linked_entity_id', newReq.id);
      fd.append('category', 'review_submission');
      fd.append('folder_path', 'Legal Requests Documents/' + newReq.ref);
      fd.append('description', 'Review submission: ' + form.subject);
      await api.uploadDocument(fd);
      showToast('Review request submitted - Legal team has been notified');
      setForm({ subject: '', review_type: '', department: '', description: '', urgency: 'medium' });
      setSubmitFile(null);
      if (submitFileRef.current) submitFileRef.current.value = '';
      loadData();
    } catch (err) {
      showToast('Failed to submit: ' + (err.response?.data?.error || err.message));
    } finally { setSubmitting(false); }
  }

  async function selectReview(review) {
    setSelectedReview(review);
    setCommentForm({ comment: '', annotation_type: 'comment' });
    setReviewFile(null);
    const [commentsRes, docsRes] = await Promise.allSettled([
      api.getReviewComments(review.id),
      api.getRequestDocuments(review.id),
    ]);
    setComments(commentsRes.status === 'fulfilled' ? commentsRes.value.data : []);
    setDocuments(docsRes.status === 'fulfilled' ? docsRes.value.data : []);
  }

  async function handleAccept() {
    if (!selectedReview) return;
    setAccepting(true);
    try {
      await api.acceptReview(selectedReview.id);
      showToast('Review accepted - requester has been notified');
      await loadData();
      const { data } = await api.getRequests({ type: 'document_review' });
      const updated = (data.data || []).find(r => r.id === selectedReview.id);
      if (updated) setSelectedReview(updated);
    } catch (err) {
      showToast('Failed to accept: ' + (err.response?.data?.error || err.message));
    } finally { setAccepting(false); }
  }

  async function handleUploadReviewed() {
    if (!reviewFile || !selectedReview) { showToast('Please select a file to upload'); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', reviewFile);
      fd.append('linked_entity_type', 'request');
      fd.append('linked_entity_id', selectedReview.id);
      fd.append('category', 'reviewed_document');
      fd.append('folder_path', 'Legal Requests Documents/' + selectedReview.ref + '/Reviewed');
      fd.append('description', 'Reviewed version uploaded by Legal');
      await api.uploadDocument(fd);
      showToast('Reviewed document uploaded successfully');
      setReviewFile(null);
      if (reviewFileRef.current) reviewFileRef.current.value = '';
      const { data } = await api.getRequestDocuments(selectedReview.id);
      setDocuments(data);
    } catch (err) {
      showToast('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally { setUploadingDoc(false); }
  }

  async function handleAddComment() {
    if (!commentForm.comment.trim() || !selectedReview) { showToast('Please enter a comment'); return; }
    setAddingComment(true);
    try {
      await api.addReviewComment(selectedReview.id, commentForm);
      const labels = {
        comment: 'Comment added',
        annotation: 'Annotation saved',
        approval: 'Review approved - requester notified',
        return_with_comments: 'Returned to requester with comments - email sent',
      };
      showToast(labels[commentForm.annotation_type] || 'Saved');
      setCommentForm({ comment: '', annotation_type: 'comment' });
      await loadData();
      const { data: updComments } = await api.getReviewComments(selectedReview.id);
      setComments(updComments);
      const { data } = await api.getRequests({ type: 'document_review' });
      const updated = (data.data || []).find(r => r.id === selectedReview.id);
      if (updated) setSelectedReview(updated);
    } catch (err) {
      showToast('Failed: ' + (err.response?.data?.error || err.message));
    } finally { setAddingComment(false); }
  }

  const statusBadge = (s) => {
    const cfg = {
      pending:   { cls: 'badge-blue',  label: 'Pending' },
      in_review: { cls: 'badge-gold',  label: 'In Review' },
      completed: { cls: 'badge-green', label: 'Completed' },
      overdue:   { cls: 'badge-red',   label: 'Overdue' },
    };
    const { cls, label } = cfg[s] || { cls: 'badge-gray', label: s };
    return <span className={`badge ${cls}`}><span className="dot"></span>{label}</span>;
  };

  const annotationIcon  = (t) => ({ comment:'[C]', annotation:'[A]', approval:'[OK]', return_with_comments:'[R]' })[t] || '[C]';
  const annotationLabel = (t) => ({ comment:'Comment', annotation:'Annotation', approval:'Approved', return_with_comments:'Returned' })[t] || t;
  const annotationCls   = (t) => ({ comment:'badge-blue', annotation:'badge-gold', approval:'badge-green', return_with_comments:'badge-red' })[t] || 'badge-gray';

  const pendingCount = reviews.filter(r => r.status === 'pending' || r.status === 'in_review').length;
  const slaBreached  = reviews.filter(r =>
    (r.status === 'pending' || r.status === 'in_review') &&
    new Date(r.created_at) < new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
  ).length;
  const userIsLegal  = isLegal(currentUser);

  const docIcon = (mime) => {
    if (!mime) return 'FILE';
    if (mime.includes('pdf')) return 'PDF';
    if (mime.includes('word') || mime.includes('docx')) return 'DOC';
    if (mime.includes('image')) return 'IMG';
    return 'FILE';
  };

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Review &amp; Legal Advice</div>
      </div>

      {slaBreached > 0 && (
        <div className="alert alert-red">
          <strong>{slaBreached} review request(s)</strong> pending legal opinion for over 5 business days.
        </div>
      )}

      <div className="row-2">
        {/* Submit for Review Form */}
        <div className="card">
          <div className="card-title">Submit for Review</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Document / Subject <span style={{color:'var(--danger)'}}>*</span></label>
              <input
                type="text"
                placeholder="Name of document or matter"
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Review Type</label>
              <select value={form.review_type} onChange={e => setForm({ ...form, review_type: e.target.value })}>
                <option value="">-- Select --</option>
                <option>Contract Review</option>
                <option>Legal Opinion</option>
                <option>Policy Review</option>
                <option>Compliance Check</option>
                <option>Risk Assessment</option>
              </select>
            </div>
            <div className="form-group">
              <label>Requesting Department</label>
              <input
                type="text"
                placeholder="Department"
                value={form.department}
                onChange={e => setForm({ ...form, department: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Urgency</label>
              <select value={form.urgency} onChange={e => setForm({ ...form, urgency: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="form-group full">
              <label>Description / Context</label>
              <textarea
                placeholder="Provide context, specific areas to review, questions for counsel..."
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="form-group full">
              <label>
                Attach Document <span style={{color:'var(--danger)'}}>*</span>
                <span style={{fontSize:11,color:'var(--muted)',fontWeight:400,marginLeft:6}}>
                  (required — PDF, Word, or image)
                </span>
              </label>
              <input
                ref={submitFileRef}
                type="file"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                onChange={e => setSubmitFile(e.target.files[0] || null)}
                style={{padding:'6px 0'}}
              />
              {submitFile ? (
                <div style={{fontSize:11,color:'var(--success)',marginTop:4}}>
                  Attached: {submitFile.name} ({(submitFile.size/1024).toFixed(0)} KB)
                </div>
              ) : (
                <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>
                  A document must be attached. Legal cannot review without the source material.
                </div>
              )}
            </div>
          </div>
          <div className="form-actions">
            <button
              className="topbar-btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </div>

        {/* SLA Dashboard */}
        <div className="card">
          <div className="card-title">Review SLA Dashboard</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div style={{textAlign:'center',padding:16,background:'var(--pale)',borderRadius:8}}>
              <div style={{fontSize:28,fontWeight:700,color:'var(--mid)'}}>{pendingCount}</div>
              <div style={{fontSize:11,color:'var(--muted)'}}>Open Reviews</div>
            </div>
            <div style={{textAlign:'center',padding:16,background:slaBreached>0?'#FEF2F2':'#ECFDF5',borderRadius:8}}>
              <div style={{fontSize:28,fontWeight:700,color:slaBreached>0?'var(--danger)':'var(--success)'}}>{slaBreached}</div>
              <div style={{fontSize:11,color:'var(--muted)'}}>SLA Breached (&gt;5 days)</div>
            </div>
            <div style={{textAlign:'center',padding:16,background:'#ECFDF5',borderRadius:8}}>
              <div style={{fontSize:28,fontWeight:700,color:'var(--success)'}}>{reviews.filter(r=>r.status==='completed').length}</div>
              <div style={{fontSize:11,color:'var(--muted)'}}>Completed</div>
            </div>
            <div style={{textAlign:'center',padding:16,background:'var(--pale)',borderRadius:8}}>
              <div style={{fontSize:28,fontWeight:700,color:'var(--mid)'}}>{reviews.filter(r=>r.status==='in_review').length}</div>
              <div style={{fontSize:11,color:'var(--muted)'}}>In Review</div>
            </div>
          </div>
          <div style={{marginTop:16,padding:12,background:'var(--pale)',borderRadius:8,fontSize:11,color:'var(--muted)',lineHeight:1.8}}>
            <strong style={{color:'var(--navy)'}}>How it works:</strong><br/>
            1. Requester submits document — Legal team notified by email<br/>
            2. Legal accepts the request — requester notified "In Review"<br/>
            3. Legal reviews and uploads annotated version to SharePoint<br/>
            4. Legal approves or returns with comments — requester emailed
          </div>
        </div>
      </div>

      {/* Review Register */}
      <div className="card" style={{marginTop:18}}>
        <div className="card-title">Review Register</div>
        {loading ? <div className="loading">Loading...</div> : (
          editMode ? (
            <EditableDataTable
              columns={[{key:'ref',label:'Ref'},{key:'subject',label:'Subject'},{key:'requester_dept',label:'Department'},{key:'status',label:'Status'}]}
              data={reviews}
              editable={true}
              onDataChange={setReviews}
              renderCell={(row, colKey) => {
                if (colKey === 'ref') return <span style={{fontSize:11,fontWeight:600,color:'var(--mid)'}}>{row.ref}</span>;
                if (colKey === 'status') return statusBadge(row.status);
                if (colKey === 'requester_dept') return row.requester_dept || '-';
                return row[colKey] ?? '';
              }}
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Ref</th><th>Subject</th><th>Department</th><th>Submitted</th>
                    <th>Reviewer</th><th>Status</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 && (
                    <tr><td colSpan={7} style={{textAlign:'center',padding:24,color:'var(--muted)',fontSize:13}}>No review requests yet.</td></tr>
                  )}
                  {reviews.map(r => (
                    <tr
                      key={r.id}
                      style={{cursor:'pointer',background:selectedReview?.id===r.id?'var(--pale)':undefined}}
                      onClick={() => selectReview(r)}
                    >
                      <td style={{fontSize:11,fontWeight:600,color:'var(--mid)'}}>{r.ref}</td>
                      <td>{r.subject}</td>
                      <td>{r.requester_dept || '-'}</td>
                      <td>{new Date(r.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</td>
                      <td>{r.assignee_name || 'Unassigned'}</td>
                      <td>{statusBadge(r.status)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <button className="sig-btn ghost" onClick={() => selectReview(r)}>Open</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* Review Workspace */}
      {selectedReview && (
        <div className="card" style={{marginTop:18}}>
          <div className="card-title" style={{display:'flex',alignItems:'center',gap:12}}>
            <span>Review Workspace</span>
            <span style={{fontSize:11,fontWeight:600,color:'var(--mid)',background:'var(--pale)',padding:'3px 10px',borderRadius:12}}>
              {selectedReview.ref}
            </span>
            <span style={{fontSize:13,color:'var(--dark)',fontWeight:400}}>{selectedReview.subject}</span>
            <div style={{marginLeft:'auto',display:'flex',gap:8,alignItems:'center'}}>
              {statusBadge(selectedReview.status)}
              <button
                className="modal-close"
                onClick={() => { setSelectedReview(null); setComments([]); setDocuments([]); }}
              >&times;</button>
            </div>
          </div>

          {/* Accept banner */}
          {userIsLegal && selectedReview.status === 'pending' && (
            <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'#EFF6FF',borderRadius:8,marginBottom:16,border:'1px solid #BFDBFE'}}>
              <span style={{fontSize:13,color:'#1E40AF'}}>
                This request is awaiting acceptance. Accept it to begin the review and notify the requester.
              </span>
              <button
                className="topbar-btn btn-primary"
                style={{marginLeft:'auto',whiteSpace:'nowrap'}}
                onClick={handleAccept}
                disabled={accepting}
              >
                {accepting ? 'Accepting...' : 'Accept for Review'}
              </button>
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>

            {/* Left: Documents */}
            <div>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:700,color:'var(--navy)',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                  Submitted Document(s)
                </div>
                {documents.filter(d => d.category === 'review_submission').length === 0 ? (
                  <p style={{fontSize:12,color:'var(--muted)',padding:'8px 0'}}>No documents attached to this request.</p>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {documents.filter(d => d.category === 'review_submission').map(doc => (
                      <div key={doc.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#F8FAFC',borderRadius:8,border:'1px solid #E2E8F0'}}>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--mid)',background:'#DBEAFE',padding:'2px 6px',borderRadius:4}}>
                          {docIcon(doc.mime_type)}
                        </span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:'var(--navy)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.name}</div>
                          <div style={{fontSize:10,color:'var(--muted)'}}>
                            {doc.uploaded_by_name} &middot; {new Date(doc.created_at).toLocaleDateString('en-GB')}
                            {doc.file_size_bytes ? ' · ' + (doc.file_size_bytes/1024).toFixed(0) + ' KB' : ''}
                          </div>
                        </div>
                        {doc.sharepoint_url ? (
                          <a href={doc.sharepoint_url} target="_blank" rel="noreferrer"
                            className="sig-btn primary" style={{fontSize:11,whiteSpace:'nowrap'}}>
                            Download
                          </a>
                        ) : (
                          <span style={{fontSize:11,color:'var(--muted)'}}>Stored</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {documents.filter(d => d.category === 'reviewed_document').length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:700,color:'#065F46',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                    Reviewed Version(s)
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {documents.filter(d => d.category === 'reviewed_document').map(doc => (
                      <div key={doc.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'#ECFDF5',borderRadius:8,border:'1px solid #A7F3D0'}}>
                        <span style={{fontSize:11,fontWeight:700,color:'#065F46',background:'#D1FAE5',padding:'2px 6px',borderRadius:4}}>
                          {docIcon(doc.mime_type)}
                        </span>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600,color:'#065F46',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{doc.name}</div>
                          <div style={{fontSize:10,color:'var(--muted)'}}>
                            Reviewed by {doc.uploaded_by_name} &middot; {new Date(doc.created_at).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                        {doc.sharepoint_url ? (
                          <a href={doc.sharepoint_url} target="_blank" rel="noreferrer"
                            className="sig-btn primary" style={{fontSize:11,whiteSpace:'nowrap'}}>
                            Download
                          </a>
                        ) : (
                          <span style={{fontSize:11,color:'var(--muted)'}}>Stored</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {userIsLegal && selectedReview.status === 'in_review' && (
                <div style={{padding:14,background:'var(--pale)',borderRadius:8,border:'1px solid var(--border)'}}>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--navy)',marginBottom:10}}>
                    Upload Reviewed / Annotated Version
                  </div>
                  <input
                    ref={reviewFileRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={e => setReviewFile(e.target.files[0] || null)}
                    style={{fontSize:12,marginBottom:8,display:'block'}}
                  />
                  {reviewFile && (
                    <div style={{fontSize:11,color:'var(--success)',marginBottom:8}}>
                      Ready: {reviewFile.name} ({(reviewFile.size/1024).toFixed(0)} KB)
                    </div>
                  )}
                  <button
                    className="topbar-btn btn-primary"
                    onClick={handleUploadReviewed}
                    disabled={!reviewFile || uploadingDoc}
                    style={{fontSize:12}}
                  >
                    {uploadingDoc ? 'Uploading...' : 'Upload to SharePoint'}
                  </button>
                </div>
              )}
            </div>

            {/* Right: Comments */}
            <div>
              <div style={{fontSize:12,fontWeight:700,color:'var(--navy)',marginBottom:10,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                Review Thread ({comments.length})
              </div>

              <div style={{display:'flex',flexDirection:'column',gap:10,maxHeight:280,overflowY:'auto',marginBottom:16}}>
                {comments.length === 0 && (
                  <p style={{fontSize:12,color:'var(--muted)',padding:'8px 0'}}>No comments yet.</p>
                )}
                {comments.map(c => (
                  <div
                    key={c.id}
                    style={{
                      padding:12,
                      background: c.annotation_type==='return_with_comments' ? '#FEF2F2'
                                : c.annotation_type==='approval' ? '#ECFDF5' : 'var(--pale)',
                      borderRadius:8,
                      borderLeft: '3px solid ' + (
                        c.annotation_type==='approval' ? 'var(--success)'
                        : c.annotation_type==='return_with_comments' ? 'var(--danger)'
                        : 'var(--mid)'
                      ),
                    }}
                  >
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <strong style={{fontSize:12,color:'var(--navy)'}}>{c.author_name || 'Reviewer'}</strong>
                      <span className={'badge ' + annotationCls(c.annotation_type)} style={{fontSize:10}}>
                        {annotationLabel(c.annotation_type)}
                      </span>
                      <span style={{fontSize:10,color:'var(--muted)',marginLeft:'auto'}}>
                        {new Date(c.created_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
                      </span>
                    </div>
                    <p style={{fontSize:12,margin:0,color:'var(--dark)',lineHeight:1.5}}>{c.comment}</p>
                  </div>
                ))}
              </div>

              {userIsLegal && selectedReview.status === 'in_review' && (
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--navy)',marginBottom:10}}>Add Review Action</div>
                  <div className="form-group" style={{marginBottom:10}}>
                    <label style={{fontSize:12}}>Action Type</label>
                    <select
                      value={commentForm.annotation_type}
                      onChange={e => setCommentForm({ ...commentForm, annotation_type: e.target.value })}
                    >
                      <option value="comment">General Comment</option>
                      <option value="annotation">Document Annotation</option>
                      <option value="approval">Approve &amp; Close Review</option>
                      <option value="return_with_comments">Return to Requester with Comments</option>
                    </select>
                  </div>
                  <div className="form-group" style={{marginBottom:10}}>
                    <label style={{fontSize:12}}>
                      {commentForm.annotation_type === 'return_with_comments' ? 'Comments for Requester (will be emailed)' :
                       commentForm.annotation_type === 'approval' ? 'Approval Note (optional, will be emailed)' : 'Comment'}
                    </label>
                    <textarea
                      rows={3}
                      placeholder={
                        commentForm.annotation_type === 'return_with_comments'
                          ? 'Describe what needs to be revised before this can proceed...'
                          : commentForm.annotation_type === 'approval'
                          ? 'Optional legal note to include in the completion email...'
                          : 'Add your review comment or observation...'
                      }
                      value={commentForm.comment}
                      onChange={e => setCommentForm({ ...commentForm, comment: e.target.value })}
                    />
                  </div>
                  {commentForm.annotation_type === 'return_with_comments' && (
                    <div className="alert alert-gold" style={{marginBottom:10,fontSize:12}}>
                      This will reset the status to Pending and email the requester with your comments.
                    </div>
                  )}
                  {commentForm.annotation_type === 'approval' && (
                    <div className="alert alert-blue" style={{marginBottom:10,fontSize:12}}>
                      This will mark the review as Completed and email the requester a completion notice.
                    </div>
                  )}
                  <button
                    className="topbar-btn btn-primary"
                    onClick={handleAddComment}
                    disabled={addingComment}
                    style={{fontSize:12}}
                  >
                    {addingComment ? 'Saving...' :
                     commentForm.annotation_type === 'approval' ? 'Approve Review' :
                     commentForm.annotation_type === 'return_with_comments' ? 'Return with Comments' : 'Add Comment'}
                  </button>
                </div>
              )}

              {(!userIsLegal || selectedReview.status !== 'in_review') && (
                <div style={{fontSize:11,color:'var(--muted)',padding:'8px 0'}}>
                  {selectedReview.status === 'completed'
                    ? 'This review is complete.'
                    : selectedReview.status === 'pending'
                    ? 'Waiting for Legal to accept this review.'
                    : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
